/**
 * The SKU reference attached to a message.
 *
 * Referenced rows are rarely adjacent in the variance table, so the reference
 * is not a single jump target: it collapses by default, expands into a capped
 * scrolling list (about ten rows, then it scrolls), and every entry jumps to
 * its own row. Expanding lights up the whole set in the table so the reviewer
 * can see where the rows sit; clicking one gives it the focus glow so it
 * stands out FROM the highlighted set, not just from the table.
 */
import { useTranslation } from 'react-i18next';
import { ArrowRight, ChevronDown, ChevronRight, Target } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { StocktakeProposalLineRow } from '@threadheads/shared';
import { formatQty, formatSigned } from '../lib/format';

interface SkuReferenceProps {
  skus: string[];
  linesBySku: ReadonlyMap<string, StocktakeProposalLineRow>;
  open: boolean;
  onToggle: () => void;
  focusedSku: string | null;
  onJump: (sku: string) => void;
}

export function SkuReference({
  skus,
  linesBySku,
  open,
  onToggle,
  focusedSku,
  onJump,
}: SkuReferenceProps) {
  const { t } = useTranslation('inventory');
  if (skus.length === 0) return null;

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-md border border-threadheads-purple/30 bg-card px-2 py-1 text-[11px] font-semibold text-threadheads-purple transition-colors duration-200 hover:bg-threadheads-purple/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
      >
        {open ? (
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
        )}
        <Target className="h-3 w-3" aria-hidden="true" />
        {t('stocktakes.messages.refCount', { count: skus.length })}
        <span aria-hidden="true" className="font-normal opacity-45">
          |
        </span>
        <span>
          {open ? t('stocktakes.messages.refHide') : t('stocktakes.messages.refShow')}
        </span>
      </button>

      {open ? (
        <div className="mt-1.5 max-h-[248px] overflow-y-auto rounded-md border bg-card">
          {skus.map((sku) => {
            const line = linesBySku.get(sku);
            const delta =
              line && line.countedQty != null ? line.countedQty - line.expectedQty : null;
            const current = focusedSku === sku;
            return (
              <button
                key={sku}
                type="button"
                onClick={() => onJump(sku)}
                className={cn(
                  'grid w-full grid-cols-[1fr_auto_auto] items-center gap-2 border-b px-2.5 py-1.5 text-left text-[11px] last:border-b-0',
                  'transition-colors duration-200 hover:bg-threadheads-purple/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring motion-reduce:transition-none',
                  current && 'bg-threadheads-purple/10 shadow-[inset_3px_0_0_0_#7E69AB]',
                )}
              >
                <span
                  className={cn(
                    'truncate font-mono',
                    current && 'font-semibold text-threadheads-purple',
                  )}
                >
                  {sku}
                </span>
                <span className="inline-flex items-center gap-1 tabular-nums text-muted-foreground">
                  {line ? (
                    <>
                      {formatQty(line.expectedQty)}
                      <ArrowRight className="h-3 w-3" aria-hidden="true" />
                      {line.countedQty == null
                        ? t('stocktakes.messages.refBlank')
                        : formatQty(line.countedQty)}
                    </>
                  ) : (
                    t('stocktakes.messages.refNotInView')
                  )}
                </span>
                <span
                  className={cn(
                    'min-w-[38px] text-right tabular-nums',
                    delta == null || delta === 0
                      ? 'text-muted-foreground'
                      : delta > 0
                        ? 'text-emerald-700'
                        : 'text-destructive',
                  )}
                >
                  {delta == null ? '' : formatSigned(delta)}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
